import { useState, useRef } from "react";
import useFocoModal from "../../hooks/useFocoModal";
import Icon from "../Icon";
import { BtnPrimary } from "../Atoms";
import { TIPO_COLORS, TIPO_ICONS } from "../../utils";
import Campo from "../forms/Campo";
import { colorNombre, iconoNombre } from "../../colorNombre";

const SCOPES = [
  { v:"nacional", label:"Nacional" },
  { v:"regional", label:"Regional" },
  { v:"local",    label:"Local"    },
];

const lbS  = {fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em"};
const inS  = {width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid var(--border)",fontSize:13,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface2)",color:"var(--text)"};
const chkS = {display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:"var(--text2)"};

// Gestión de calendarios: crear/editar/borrar, importar festivos en bloque y
// duplicar el año. El calendario de empresa solo lo toca un admin; el backend
// lo vuelve a comprobar, aquí solo se oculta lo que no se puede hacer.
export default function CalendariosModal({
  calendarios = [], isAdmin = false,
  onCreate, onUpdate, onDelete, onImportar, onDuplicarAnio, onClose,
}) {
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, onClose);
  const [editing, setEditing] = useState(null);   // objeto calendario o {nuevo:true}
  const [panel,   setPanel  ] = useState(null);   // {tipo:'importar'|'duplicar', cal}
  const [busy,    setBusy   ] = useState(false);

  const empresa   = calendarios.filter(c =>  c.empresa);
  const personales= calendarios.filter(c => !c.empresa);

  const del = async (cal) => {
    if (busy) return;
    setBusy(true);
    try {
      await onDelete(cal.id, false);
    } catch (e) {
      // 409: el backend informa de cuántos eventos se perderían
      if (window.confirm(`${e.message}\n\n¿Eliminar el calendario y sus eventos?`))
        await onDelete(cal.id, true);
    } finally { setBusy(false); }
  };

  const Fila = ({ cal }) => (
    <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",borderRadius:9,
      background:"var(--surface2)",border:"1px solid var(--border)",marginBottom:6}}>
      <span style={{width:10,height:10,borderRadius:3,background:cal.color||"#94a3b8",flexShrink:0}}/>
      {cal.icon && <Icon name={cal.icon} size={13} color={cal.color||"var(--text2)"}/>}
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12.5,fontWeight:600,color:"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {cal.name}
        </div>
        <div style={{fontSize:10,color:"var(--text3)",marginTop:1}}>
          {cal.kind === "laboral" ? "Laboral" : "Personal"}
          {cal.scope ? ` · ${SCOPES.find(s=>s.v===cal.scope)?.label || cal.scope}` : ""}
          {` · ${cal.event_count} evento${cal.event_count === 1 ? "" : "s"}`}
          {cal.archived ? " · Archivado" : ""}
        </div>
      </div>
      {cal.can_edit ? (
        <div style={{display:"flex",gap:4,flexShrink:0}}>
          <button aria-label="Importar festivos" className="btn" title="Importar festivos" onClick={()=>{setPanel({tipo:"importar",cal});setEditing(null);}}
            style={btnIcoS}><Icon name="download" size={12} color="var(--text2)"/></button>
          <button aria-label="Duplicar año" className="btn" title="Duplicar año" onClick={()=>{setPanel({tipo:"duplicar",cal});setEditing(null);}}
            style={btnIcoS}><Icon name="arrow-repeat" size={12} color="var(--text2)"/></button>
          <button aria-label="Editar" className="btn" title="Editar" onClick={()=>{setEditing(cal);setPanel(null);}}
            style={btnIcoS}><Icon name="pencil" size={12} color="var(--text2)"/></button>
          <button aria-label="Eliminar" className="btn" title="Eliminar" onClick={()=>del(cal)} disabled={busy}
            style={btnIcoS}><Icon name="trash3" size={12} color="var(--red-text)"/></button>
        </div>
      ) : (
        <span style={{fontSize:10,color:"var(--text3)",flexShrink:0}}>Solo lectura</span>
      )}
    </div>
  );

  return (
    <div className="modal-bg"
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,.38)",backdropFilter:"blur(3px)",zIndex:160,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={onClose}>
      <div ref={cajaRef} role="dialog" aria-modal="true" className="modal-box"
        style={{background:"var(--surface)",borderRadius:16,boxShadow:"var(--shadow-lg)",width:"100%",maxWidth:560,padding:24,maxHeight:"90vh",overflowY:"auto"}}
        onClick={e=>e.stopPropagation()}>

        <h3 style={{margin:"0 0 4px",fontWeight:700,fontSize:16,display:"flex",alignItems:"center",gap:8}}>
          <Icon name="calendar3" size={15} color="var(--accent)"/> Gestionar calendarios
        </h3>
        <p style={{margin:"0 0 18px",fontSize:11.5,color:"var(--text3)",lineHeight:1.5}}>
          El calendario laboral de empresa marca los festivos que descuentan días
          trabajados. Además puedes crear calendarios propios para tus eventos.
        </p>

        {empresa.length > 0 && (
          <div style={{marginBottom:16}}>
            <span style={lbS}>De empresa</span>
            {empresa.map(c => <Fila key={c.id} cal={c}/>)}
          </div>
        )}

        <div style={{marginBottom:16}}>
          <span style={lbS}>Mis calendarios</span>
          {personales.length
            ? personales.map(c => <Fila key={c.id} cal={c}/>)
            : <div style={{fontSize:11.5,color:"var(--text3)",padding:"6px 2px"}}>Todavía no tienes calendarios propios.</div>}
        </div>

        {!editing && !panel && (
          <button className="btn" onClick={()=>setEditing({ nuevo:true })}
            style={{padding:"8px 14px",borderRadius:9,border:"1px dashed var(--border)",cursor:"pointer",
              fontSize:12.5,background:"transparent",color:"var(--accent)",fontFamily:"var(--font)",
              display:"flex",alignItems:"center",gap:6,width:"100%",justifyContent:"center",marginBottom:16}}>
            <Icon name="plus-circle" size={13} color="var(--accent)"/> Nuevo calendario
          </button>
        )}

        {editing && (
          <FormCalendario cal={editing.nuevo ? null : editing} isAdmin={isAdmin}
            onCancel={()=>setEditing(null)}
            onSave={async d => {
              if (editing.nuevo) await onCreate(d);
              else                await onUpdate(editing.id, d);
              setEditing(null);
            }}/>
        )}

        {panel?.tipo === "importar" && (
          <PanelImportar cal={panel.cal} onCancel={()=>setPanel(null)}
            onImportar={async (text, anual) => { await onImportar(panel.cal.id, text, anual); setPanel(null); }}/>
        )}

        {panel?.tipo === "duplicar" && (
          <PanelDuplicar cal={panel.cal} onCancel={()=>setPanel(null)}
            onDuplicar={async (from, to) => { await onDuplicarAnio(panel.cal.id, from, to); setPanel(null); }}/>
        )}

        <div style={{display:"flex",justifyContent:"flex-end",marginTop:6}}>
          <BtnPrimary onClick={onClose}>Cerrar</BtnPrimary>
        </div>
      </div>
    </div>
  );
}

const btnIcoS = {padding:"5px 7px",borderRadius:7,border:"1px solid var(--border)",background:"var(--surface)",cursor:"pointer",display:"flex",alignItems:"center"};
const cajaS   = {padding:14,borderRadius:11,background:"var(--surface2)",border:"1px solid var(--border)",marginBottom:16};

function FormCalendario({ cal, isAdmin, onSave, onCancel }) {
  const isNew = !cal;
  const [f, setF] = useState({
    name:    cal?.name  || "",
    color:   cal?.color || TIPO_COLORS[8],
    icon:    cal?.icon  || "calendar3",
    kind:    cal?.kind  || (isNew ? "personal" : "personal"),
    scope:   cal?.scope || "",
    empresa: cal ? !!cal.empresa : false,
    archived:!!cal?.archived,
  });
  const [showIcons, setShowIcons] = useState(false);
  const set = (k,v) => setF(p => ({...p,[k]:v}));
  const valid = !!f.name.trim();

  return (
    <div style={cajaS}>
      <div style={{marginBottom:12}}>
        <Campo><label style={lbS}>{isNew ? "Nuevo calendario" : "Editar calendario"}</label>
        <input value={f.name} onChange={e=>set("name", e.target.value)} style={inS}
          placeholder="Festivos Madrid, Mis vacaciones…" autoFocus/></Campo>
      </div>

      <div style={{display:"flex",gap:10,marginBottom:12}}>
        <div style={{flex:1}}>
          <Campo><label style={lbS}>Tipo</label>
          <select value={f.kind} onChange={e=>set("kind", e.target.value)} style={inS}>
            <option value="personal">Eventos</option>
            <option value="laboral">Laboral (festivos)</option>
          </select></Campo>
        </div>
        {f.kind === "laboral" && (
          <div style={{flex:1}}>
            <Campo><label style={lbS}>Ámbito</label>
            <select value={f.scope} onChange={e=>set("scope", e.target.value)} style={inS}>
              <option value="">Sin ámbito</option>
              {SCOPES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select></Campo>
          </div>
        )}
      </div>

      <div style={{marginBottom:12}}>
        <Campo><label style={lbS}>Color</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
          {TIPO_COLORS.map(c => (
            <button key={c} className="btn" onClick={()=>set("color", c)} aria-label={"Color "+colorNombre(c)} aria-pressed={f.color===c}
              style={{width:22,height:22,borderRadius:6,background:c,cursor:"pointer",
                border:f.color===c?"2px solid var(--text)":"1px solid var(--border)"}}/>
          ))}
        </div></Campo>
      </div>

      <div style={{marginBottom:12}}>
        <Campo><label style={lbS}>Icono</label>
        <button className="btn" onClick={()=>setShowIcons(p=>!p)}
          style={{...inS,display:"flex",alignItems:"center",gap:8,cursor:"pointer",textAlign:"left"}}>
          <Icon name={f.icon || "calendar3"} size={14} color={f.color}/>
          <span style={{flex:1,fontSize:12,color:"var(--text2)"}}>{f.icon || "Sin icono"}</span>
          <Icon name={showIcons ? "chevron-up" : "chevron-down"} size={11} color="var(--text3)"/>
        </button></Campo>
        {showIcons && (
          <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:8,maxHeight:110,overflowY:"auto"}}>
            {TIPO_ICONS.map(ic => (
              <button key={ic} className="btn" onClick={()=>{set("icon", ic);setShowIcons(false);}} aria-label={"Icono "+iconoNombre(ic)} aria-pressed={f.icon===ic}
                style={{padding:6,borderRadius:7,cursor:"pointer",background:f.icon===ic?"var(--accent-bg)":"var(--surface)",
                  border:`1px solid ${f.icon===ic?"var(--accent)":"var(--border)"}`,display:"flex"}}>
                <Icon name={ic} size={13} color={f.icon===ic?"var(--accent)":"var(--text2)"}/>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Solo un admin puede publicar un calendario para toda la empresa */}
      {isAdmin && isNew && (
        <label style={{...chkS,marginBottom:12}}>
          <input type="checkbox" checked={f.empresa} onChange={e=>set("empresa", e.target.checked)}
            style={{accentColor:"var(--accent)",width:14,height:14}}/>
          <Icon name="building" size={12} color="var(--text3)"/>
          Calendario de empresa (lo verán todos los usuarios)
        </label>
      )}

      {!isNew && (
        <label style={{...chkS,marginBottom:12}}>
          <input type="checkbox" checked={f.archived} onChange={e=>set("archived", e.target.checked)}
            style={{accentColor:"var(--accent)",width:14,height:14}}/>
          Archivar (deja de mostrarse en el calendario)
        </label>
      )}

      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button className="btn" onClick={onCancel}
          style={{padding:"7px 14px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",fontSize:12.5,background:"var(--surface)",color:"var(--text2)",fontFamily:"var(--font)"}}>
          Cancelar
        </button>
        <BtnPrimary disabled={!valid}
          onClick={()=>valid && onSave({
            name: f.name.trim(), color: f.color, icon: f.icon,
            kind: f.kind, scope: f.kind === "laboral" ? (f.scope || null) : null,
            ...(isNew ? { empresa: f.empresa } : { archived: f.archived }),
          })}>
          {isNew ? "Crear" : "Guardar"}
        </BtnPrimary>
      </div>
    </div>
  );
}

function PanelImportar({ cal, onImportar, onCancel }) {
  const [text,  setText ] = useState("");
  const [anual, setAnual] = useState(cal.kind === "laboral");
  const [busy,  setBusy ] = useState(false);
  const lineas = text.split(/\r?\n/).filter(l => l.trim()).length;

  const run = async () => {
    if (!lineas || busy) return;
    setBusy(true);
    try { await onImportar(text, anual); } finally { setBusy(false); }
  };

  return (
    <div style={cajaS}>
      <label style={lbS} htmlFor="cal-import-texto">Importar en «{cal.name}»</label>
      <p style={{fontSize:11,color:"var(--text3)",lineHeight:1.5,margin:"0 0 8px"}}>
        Una línea por fecha, con el formato <b>AAAA-MM-DD;Nombre</b>. Opcionalmente
        un tercer campo con el tipo (festivo, evento, tarea, ausencia). Por defecto
        se importan como festivos.
      </p>
      <textarea id="cal-import-texto" value={text} onChange={e=>setText(e.target.value)}
        placeholder={"2027-01-01;Año Nuevo\n2027-05-02;Fiesta de la Comunidad\n2027-05-15;San Isidro"}
        style={{...inS,minHeight:110,resize:"vertical",fontFamily:"var(--mono)",fontSize:11.5}}/>
      <label style={{...chkS,margin:"10px 0"}}>
        <input type="checkbox" checked={anual} onChange={e=>setAnual(e.target.checked)}
          style={{accentColor:"var(--accent)",width:14,height:14}}/>
        Marcar como festivos fijos que se repiten cada año
      </label>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",alignItems:"center"}}>
        <span style={{flex:1,fontSize:11,color:"var(--text3)"}}>{lineas} línea{lineas===1?"":"s"}</span>
        <button className="btn" onClick={onCancel}
          style={{padding:"7px 14px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",fontSize:12.5,background:"var(--surface)",color:"var(--text2)",fontFamily:"var(--font)"}}>
          Cancelar
        </button>
        <BtnPrimary onClick={run} disabled={!lineas || busy}>{busy ? "Importando…" : "Importar"}</BtnPrimary>
      </div>
    </div>
  );
}

function PanelDuplicar({ cal, onDuplicar, onCancel }) {
  const actual = new Date().getFullYear();
  const [from, setFrom] = useState(actual);
  const [to,   setTo  ] = useState(actual + 1);
  const [busy, setBusy] = useState(false);
  const anios = Array.from({ length: 9 }, (_, i) => actual - 3 + i);

  const run = async () => {
    if (from === to || busy) return;
    setBusy(true);
    try { await onDuplicar(from, to); } finally { setBusy(false); }
  };

  return (
    <div style={cajaS}>
      <div style={lbS}>Duplicar año en «{cal.name}»</div>
      <p style={{fontSize:11,color:"var(--text3)",lineHeight:1.5,margin:"0 0 10px"}}>
        Copia los festivos de fecha variable de un año al otro para poder ajustarlos
        después. Los que ya se repiten cada año no se copian, y las fechas que ya
        existan se omiten: puedes ejecutarlo varias veces sin duplicar nada.
      </p>
      <div style={{display:"flex",gap:10,alignItems:"flex-end",marginBottom:12}}>
        <div style={{flex:1}}>
          <Campo><label style={lbS}>Desde</label>
          <select value={from} onChange={e=>setFrom(parseInt(e.target.value,10))} style={inS}>
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select></Campo>
        </div>
        <Icon name="chevron-right" size={13} color="var(--text3)"/>
        <div style={{flex:1}}>
          <Campo><label style={lbS}>Hasta</label>
          <select value={to} onChange={e=>setTo(parseInt(e.target.value,10))} style={inS}>
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select></Campo>
        </div>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button className="btn" onClick={onCancel}
          style={{padding:"7px 14px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",fontSize:12.5,background:"var(--surface)",color:"var(--text2)",fontFamily:"var(--font)"}}>
          Cancelar
        </button>
        <BtnPrimary onClick={run} disabled={from === to || busy}>{busy ? "Copiando…" : "Duplicar"}</BtnPrimary>
      </div>
    </div>
  );
}
